import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';

const CW = 2400;
const CH = 1800;
const PC_W = 64;
const PC_H = 66;
const GRID = 72;
const MIN_RW = 200;
const MIN_RH = 150;

const DEVICE_DIMS  = { pc: { w: 64, h: 66 }, billiard: { w: 130, h: 72 }, playstation: { w: 76, h: 76 } };
const DEVICE_ICONS = { pc: '🖥', billiard: '🎱', playstation: '🎮' };
const DEVICE_COL   = { billiard: '#10b981', playstation: '#8b5cf6' };
const getDims = c => DEVICE_DIMS[c?.type] || DEVICE_DIMS.pc;

const PALETTE = [
  { bg: 'rgba(99,102,241,0.07)',  border: '#6366f1', text: '#818cf8' },
  { bg: 'rgba(16,185,129,0.07)',  border: '#10b981', text: '#34d399' },
  { bg: 'rgba(245,158,11,0.07)', border: '#f59e0b', text: '#fbbf24' },
  { bg: 'rgba(239,68,68,0.07)',  border: '#ef4444', text: '#f87171' },
  { bg: 'rgba(236,72,153,0.07)', border: '#ec4899', text: '#f472b6' },
  { bg: 'rgba(14,165,233,0.07)', border: '#0ea5e9', text: '#38bdf8' },
];

const PC_COL = { available: '#22c55e', busy: '#f97316', maintenance: '#ef4444', offline: '#6b7280' };
const pcCol  = c => c.is_broken ? '#ef4444' : (PC_COL[c.status] || '#6b7280');
const snap   = v => Math.round(v / GRID) * GRID;
const clamp  = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const STATUS_LEGEND = [
  ['#22c55e', 'layoutStatusAvailable'],
  ['#f97316', 'layoutStatusBusy'],
  ['#ef4444', 'layoutStatusBroken'],
  ['#6b7280', 'layoutStatusOffline'],
];

export default function LayoutEditor() {
  const { t } = useLang();
  const [labId, setLabId]     = useState('');
  const [rooms, setRooms]     = useState([]);
  const [computers, setComputers] = useState([]);
  const [pcPos, setPcPos]     = useState({});
  const [rPos, setRPos]       = useState({});
  const [dirty, setDirty]     = useState(false);
  const [saving, setSaving]   = useState(false);
  const [addMode, setAddMode] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(0);
  const [newRate, setNewRate] = useState('');
  const [editId, setEditId]   = useState(null);
  const [editName, setEditName] = useState('');
  const [editRate, setEditRate] = useState('');
  const [colorPickerId, setColorPickerId] = useState(null);
  const [zoom, setZoom] = useState(1);

  const drag = useRef(null);
  const zoomRef = useRef(1);
  const scrollRef = useRef(null);

  useEffect(() => { zoomRef.current = zoom; }, [zoom]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    function onWheel(e) {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom(z => Math.min(2, Math.max(0.3, parseFloat((z + delta).toFixed(1)))));
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    api.get('/owner/labs').then(r => {
      if (r.data.length) setLabId(localStorage.getItem('selected_lab_id') || String(r.data[0].id));
    });
  }, []);

  const load = useCallback(() => {
    if (!labId) return;
    api.get(`/owner/computers/layout?lab_id=${labId}`).then(r => {
      const { rooms: rs, computers: cs } = r.data;
      setRooms(rs);
      setComputers(cs);
      const pp = {};
      cs.forEach(c => { pp[c.id] = { x: c.pos_x != null ? +c.pos_x : null, y: c.pos_y != null ? +c.pos_y : null, room_id: c.room_id ?? null }; });
      setPcPos(pp);
      const rp = {};
      rs.forEach(r => { rp[r.id] = { x: +r.pos_x || 20, y: +r.pos_y || 20, w: +r.width || 320, h: +r.height || 260 }; });
      setRPos(rp);
      setDirty(false);
    });
  }, [labId]);

  useEffect(() => { load(); }, [load]);

  const detectRoom = useCallback((x, y, w = PC_W, h = PC_H) => {
    for (const rm of rooms) {
      const rp = rPos[rm.id];
      if (!rp) continue;
      if (x + w/2 >= rp.x && x + w/2 <= rp.x + rp.w &&
          y + h/2 >= rp.y && y + h/2 <= rp.y + rp.h) return rm.id;
    }
    return null;
  }, [rooms, rPos]);

  useEffect(() => {
    function onMove(e) {
      const d = drag.current;
      if (!d) return;
      const dx = (e.clientX - d.startMX) / zoomRef.current;
      const dy = (e.clientY - d.startMY) / zoomRef.current;

      if (d.type === 'pc') {
        const nx = clamp(snap(d.origX + dx), 0, CW - d.w);
        const ny = clamp(snap(d.origY + dy), 0, CH - d.h);
        setPcPos(p => ({ ...p, [d.id]: { ...p[d.id], x: nx, y: ny } }));
        setDirty(true);
      } else if (d.type === 'room') {
        const nx = Math.max(0, d.origX + dx);
        const ny = Math.max(0, d.origY + dy);
        setRPos(p => ({ ...p, [d.id]: { ...p[d.id], x: nx, y: ny } }));
        setPcPos(p => {
          const n = { ...p };
          d.pcOff.forEach(({ pcId, dx: ox, dy: oy, w, h }) => {
            n[pcId] = { ...n[pcId], x: clamp(snap(nx + ox), 0, CW - w), y: clamp(snap(ny + oy), 0, CH - h) };
          });
          return n;
        });
        setDirty(true);
      } else if (d.type === 'resize') {
        setRPos(p => ({
          ...p, [d.id]: { ...p[d.id], w: Math.max(MIN_RW, d.origW + dx), h: Math.max(MIN_RH, d.origH + dy) }
        }));
        setDirty(true);
      }
    }

    function onUp() {
      if (drag.current?.type === 'pc') {
        const { id, w, h } = drag.current;
        setPcPos(p => {
          const cur = p[id];
          if (!cur) return p;
          return { ...p, [id]: { ...cur, room_id: detectRoom(cur.x, cur.y, w, h) } };
        });
      }
      drag.current = null;
    }

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [detectRoom]);

  function startPcDrag(e, id) {
    e.preventDefault(); e.stopPropagation();
    const p = pcPos[id];
    const { w, h } = getDims(computers.find(c => c.id === id));
    drag.current = { type: 'pc', id, w, h, startMX: e.clientX, startMY: e.clientY, origX: p?.x ?? 0, origY: p?.y ?? 0 };
  }

  function startRoomDrag(e, id) {
    e.preventDefault(); e.stopPropagation();
    const rp = rPos[id];
    const pcOff = Object.entries(pcPos)
      .filter(([, p]) => p.room_id === id && p.x != null)
      .map(([pcId, p]) => {
        const { w, h } = getDims(computers.find(c => c.id === +pcId));
        return { pcId: +pcId, dx: p.x - rp.x, dy: p.y - rp.y, w, h };
      });
    drag.current = { type: 'room', id, startMX: e.clientX, startMY: e.clientY, origX: rp.x, origY: rp.y, pcOff };
  }

  function startResize(e, id) {
    e.preventDefault(); e.stopPropagation();
    const rp = rPos[id];
    drag.current = { type: 'resize', id, startMX: e.clientX, startMY: e.clientY, origW: rp.w, origH: rp.h };
  }

  function placePC(pcId) {
    if (!rooms.length) return alert(t('layoutAlertAddRoom'));
    const rm = rooms[0];
    const rp = rPos[rm.id];
    const { w, h } = getDims(computers.find(c => c.id === pcId));
    const taken = new Set(Object.values(pcPos).filter(p => p.x != null).map(p => `${p.x}_${p.y}`));
    for (let row = 0; row < 10; row++) {
      for (let col = 0; col < 10; col++) {
        const x = snap(rp.x + 8 + col * GRID);
        const y = snap(rp.y + 32 + row * GRID);
        if (x + w <= rp.x + rp.w && y + h <= rp.y + rp.h && !taken.has(`${x}_${y}`)) {
          setPcPos(p => ({ ...p, [pcId]: { x, y, room_id: rm.id } }));
          setDirty(true);
          return;
        }
      }
    }
    alert(t('layoutAlertNoSpace'));
  }

  function removePC(pcId) {
    setPcPos(p => ({ ...p, [pcId]: { x: null, y: null, room_id: null } }));
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    try {
      await api.put('/owner/computers/layout', {
        positions: Object.entries(pcPos).map(([id, p]) => ({ id: +id, pos_x: p.x, pos_y: p.y, room_id: p.room_id })),
        rooms: rooms.map(r => { const rp = rPos[r.id] || {}; return { id: r.id, pos_x: rp.x, pos_y: rp.y, width: rp.w, height: rp.h }; }),
      });
      setDirty(false);
    } catch (e) { alert(e.response?.data?.error || t('layoutAlertSaveError')); }
    setSaving(false);
  }

  async function addRoom() {
    if (!newName.trim()) return;
    let nx = 20, ny = 20;
    if (rooms.length) {
      const last = rooms[rooms.length - 1];
      const lrp = rPos[last.id] || { x: 20, y: 20, w: 320, h: 260 };
      nx = lrp.x + lrp.w + 20;
      ny = lrp.y;
      if (nx + 320 > CW - 40) { nx = 20; ny = lrp.y + lrp.h + 20; }
    }
    const payload = { lab_id: +labId, name: newName.trim(), pos_x: nx, pos_y: ny, color_index: newColor };
    if (newRate !== '') payload.hourly_rate = newRate === '' ? null : parseFloat(newRate) || null;
    const r = await api.post('/owner/rooms', { ...payload });
    // POST /owner/rooms doesn't accept hourly_rate yet — set it with PUT if provided
    let roomData = r.data;
    if (newRate !== '' && parseFloat(newRate) > 0) {
      const rr = await api.put(`/owner/rooms/${r.data.id}`, { hourly_rate: parseFloat(newRate) });
      roomData = { ...roomData, hourly_rate: rr.data.hourly_rate };
    }
    setRooms(p => [...p, roomData]);
    setRPos(p => ({ ...p, [roomData.id]: { x: nx, y: ny, w: 320, h: 260 } }));
    setNewName(''); setNewColor(0); setNewRate(''); setAddMode(false); setDirty(true);
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({ left: Math.max(0, nx * zoom - 60), top: Math.max(0, ny * zoom - 60), behavior: 'smooth' });
      }
    }, 100);
  }

  async function renameRoom(id) {
    if (!editName.trim()) return;
    const body = { name: editName.trim() };
    if (editRate !== '') body.hourly_rate = editRate === '' ? null : parseFloat(editRate) || null;
    const r = await api.put(`/owner/rooms/${id}`, body);
    setRooms(p => p.map(rm => rm.id === id ? { ...rm, name: r.data.name, hourly_rate: r.data.hourly_rate } : rm));
    setEditId(null); setEditRate('');
  }

  async function changeRoomColor(id, colorIndex) {
    await api.put(`/owner/rooms/${id}`, { color_index: colorIndex });
    setRooms(p => p.map(rm => rm.id === id ? { ...rm, color_index: colorIndex } : rm));
    setColorPickerId(null);
  }

  async function deleteRoom(id) {
    if (!window.confirm(t('layoutConfirmDeleteRoom'))) return;
    await api.delete(`/owner/rooms/${id}`);
    setRooms(p => p.filter(r => r.id !== id));
    setRPos(p => { const n = { ...p }; delete n[id]; return n; });
    setPcPos(p => { const n = { ...p }; Object.keys(n).forEach(k => { if (n[k].room_id === id) n[k] = { x: null, y: null, room_id: null }; }); return n; });
    setDirty(true);
  }

  const placed   = computers.filter(c => pcPos[c.id]?.x != null);
  const unplaced = computers.filter(c => !pcPos[c.id] || pcPos[c.id].x == null);

  return (
    <div style={{ maxWidth: 1300 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontFamily: 'Orbitron, sans-serif' }}>{t('layoutTitle')}</h1>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text3)' }}>
            {t('layoutSubtitle')} · {placed.length}/{computers.length} {t('layoutPlaced')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {addMode ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {PALETTE.map((pal, i) => (
                <div key={i} onClick={() => setNewColor(i)} style={{
                  width: 18, height: 18, borderRadius: 4, background: pal.border, cursor: 'pointer', flexShrink: 0,
                  border: newColor === i ? '2px solid #fff' : '2px solid transparent',
                  boxShadow: newColor === i ? `0 0 6px ${pal.border}` : 'none',
                  transition: 'all 0.12s',
                }} />
              ))}
              <input value={newName} onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addRoom()}
                placeholder={t('layoutRoomNamePlh')} autoFocus style={{
                  padding: '7px 10px', borderRadius: 8, fontSize: 13, width: 120,
                  background: 'var(--bg2)', border: `1px solid ${PALETTE[newColor].border}`, color: 'var(--text)', outline: 'none',
                }} />
              <input value={newRate} onChange={e => setNewRate(e.target.value)} type="number" min="0"
                placeholder="Narx (so'm/soat)" style={{
                  padding: '7px 10px', borderRadius: 8, fontSize: 13, width: 130,
                  background: 'var(--bg2)', border: `1px solid ${PALETTE[newColor].border}`, color: 'var(--text)', outline: 'none',
                }} />
              <button onClick={addRoom} style={{ padding: '7px 14px', borderRadius: 8, background: PALETTE[newColor].border, border: 'none', color: '#000', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>+</button>
              <button onClick={() => { setAddMode(false); setNewName(''); setNewColor(0); setNewRate(''); }} style={{ padding: '7px 10px', borderRadius: 8, background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text3)', cursor: 'pointer', fontSize: 13 }}>✕</button>
            </div>
          ) : (
            <button onClick={() => setAddMode(true)} style={{ padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, background: 'transparent', border: '1px dashed var(--border)', color: 'var(--text3)' }}>
              {t('layoutAddRoom')}
            </button>
          )}
          <button onClick={save} disabled={!dirty || saving} style={{
            padding: '8px 20px', borderRadius: 10, fontWeight: 700, fontSize: 13, border: 'none',
            cursor: dirty ? 'pointer' : 'default',
            background: dirty ? 'linear-gradient(135deg,var(--purple),var(--cyan))' : 'var(--bg2)',
            color: dirty ? '#fff' : 'var(--text3)',
          }}>{saving ? t('savingLabel') : dirty ? `💾 ${t('save')}` : t('layoutSavedState')}</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 14 }}>
        {/* Scrollable canvas wrapper */}
        <div ref={scrollRef} style={{
          flex: 1, height: 600, overflow: 'auto',
          border: '1px solid var(--border)', borderRadius: 14,
          background: '#0c0c13',
        }}>
          <div style={{ width: CW * zoom, height: CH * zoom, position: 'relative' }}>
          <div style={{
            position: 'absolute', width: CW, height: CH,
            transform: `scale(${zoom})`, transformOrigin: 'top left',
            backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.035) 1px, transparent 1px)`,
            backgroundSize: `${GRID}px ${GRID}px`,
            backgroundPosition: '0 0',
          }}>
            {/* Room zones */}
            {rooms.map((rm, idx) => {
              const rp = rPos[rm.id];
              if (!rp) return null;
              const pal = PALETTE[rm.color_index ?? idx % PALETTE.length];
              const roomPcCount = Object.values(pcPos).filter(p => p.room_id === rm.id).length;
              return (
                <div key={rm.id} style={{
                  position: 'absolute', left: rp.x, top: rp.y, width: rp.w, height: rp.h,
                  background: pal.bg, border: `1.5px solid ${pal.border}`,
                  borderRadius: 12, userSelect: 'none', zIndex: 1,
                }}>
                  <div onMouseDown={e => startRoomDrag(e, rm.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px',
                    cursor: 'grab', background: `${pal.border}18`,
                    borderRadius: '10px 10px 0 0',
                    borderBottom: `1px solid ${pal.border}30`,
                  }}>
                    <span style={{ fontSize: 10 }}>⠿</span>
                    {editId === rm.id ? (
                      <>
                        <input value={editName} onChange={e => setEditName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') renameRoom(rm.id); if (e.key === 'Escape') { setEditId(null); setEditRate(''); } }}
                          onMouseDown={e => e.stopPropagation()}
                          autoFocus style={{
                            background: 'transparent', border: 'none', borderBottom: `1px solid ${pal.border}`,
                            color: pal.text, fontSize: 12, fontWeight: 700, outline: 'none', width: 80, lineHeight: 1,
                          }} />
                        <input value={editRate} onChange={e => setEditRate(e.target.value)} type="number" min="0"
                          onKeyDown={e => { if (e.key === 'Enter') renameRoom(rm.id); if (e.key === 'Escape') { setEditId(null); setEditRate(''); } }}
                          onMouseDown={e => e.stopPropagation()}
                          placeholder="Narx/soat" style={{
                            background: 'transparent', border: 'none', borderBottom: `1px solid ${pal.border}`,
                            color: pal.text, fontSize: 11, outline: 'none', width: 70, lineHeight: 1,
                          }} />
                      </>
                    ) : (
                      <span style={{ fontSize: 12, fontWeight: 700, color: pal.text, flex: 1, lineHeight: 1 }}>
                        {rm.name}
                        {rm.hourly_rate && <span style={{ fontWeight: 400, fontSize: 10, marginLeft: 4, opacity: 0.75 }}>{Number(rm.hourly_rate).toLocaleString('ru-RU')}/soat</span>}
                      </span>
                    )}
                    <span style={{ fontSize: 10, color: pal.text + '99', marginRight: 2 }}>{roomPcCount} PC</span>
                    <button onMouseDown={e => e.stopPropagation()}
                      onClick={() => { setEditId(editId === rm.id ? null : rm.id); setEditName(rm.name); setEditRate(rm.hourly_rate != null ? String(rm.hourly_rate) : ''); setColorPickerId(null); }}
                      title={t('layoutRenameBtn')}
                      style={{ padding: '0 3px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: pal.text + '88', lineHeight: 1 }}>✏️</button>
                    <div style={{ position: 'relative', display: 'inline-block' }} onMouseDown={e => e.stopPropagation()}>
                      <div onClick={() => setColorPickerId(colorPickerId === rm.id ? null : rm.id)}
                        title={t('layoutColorBtn')}
                        style={{ width: 10, height: 10, borderRadius: 2, background: pal.border, cursor: 'pointer', margin: '0 2px' }} />
                      {colorPickerId === rm.id && (
                        <div style={{
                          position: 'absolute', top: 16, right: 0, zIndex: 100,
                          background: '#1a1a2e', border: '1px solid var(--border)',
                          borderRadius: 8, padding: 6, display: 'flex', gap: 5,
                          boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                        }}>
                          {PALETTE.map((p, i) => (
                            <div key={i} onClick={() => changeRoomColor(rm.id, i)} style={{
                              width: 18, height: 18, borderRadius: 4, background: p.border, cursor: 'pointer',
                              border: (rm.color_index ?? idx % PALETTE.length) === i ? '2px solid #fff' : '2px solid transparent',
                              boxShadow: (rm.color_index ?? idx % PALETTE.length) === i ? `0 0 6px ${p.border}` : 'none',
                              transition: 'all 0.1s',
                            }} />
                          ))}
                        </div>
                      )}
                    </div>
                    <button onMouseDown={e => e.stopPropagation()} onClick={() => deleteRoom(rm.id)}
                      title={t('layoutDeleteRoomBtn')}
                      style={{ padding: '0 3px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: '#ef444488', lineHeight: 1 }}>✕</button>
                  </div>
                  <div onMouseDown={e => startResize(e, rm.id)} style={{
                    position: 'absolute', right: 0, bottom: 0, width: 18, height: 18,
                    cursor: 'nwse-resize', zIndex: 2,
                    background: `linear-gradient(135deg, transparent 50%, ${pal.border}66 50%)`,
                    borderRadius: '0 0 10px 0',
                  }} />
                </div>
              );
            })}

            {/* PC/station cards */}
            {placed.map(c => {
              const p = pcPos[c.id];
              if (!p || p.x == null) return null;
              const statusCol = pcCol(c);
              const typeCol = DEVICE_COL[c.type];
              const borderCol = typeCol || statusCol;
              const { w, h } = getDims(c);
              const icon = DEVICE_ICONS[c.type] || '🖥';
              return (
                <div key={c.id} onMouseDown={e => startPcDrag(e, c.id)} style={{
                  position: 'absolute', left: p.x, top: p.y,
                  width: w, height: h,
                  cursor: 'grab', userSelect: 'none', zIndex: 10,
                  background: typeCol ? `${typeCol}18` : 'rgba(20,20,30,0.95)',
                  border: `2px solid ${borderCol}`,
                  borderRadius: c.type === 'billiard' ? 14 : 8,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                  boxShadow: `0 2px 10px ${borderCol}44`,
                }}>
                  <div style={{ fontSize: c.type === 'billiard' ? 22 : 14, lineHeight: 1 }}>{icon}</div>
                  <div style={{ fontWeight: 800, fontSize: 12, color: '#fff', lineHeight: 1 }}>#{c.number}</div>
                  {!typeCol && <div style={{ width: 6, height: 6, borderRadius: '50%', background: statusCol, marginTop: 1 }} />}
                  <button onMouseDown={e => e.stopPropagation()} onClick={() => removePC(c.id)} style={{
                    position: 'absolute', top: 2, right: 2, width: 13, height: 13,
                    borderRadius: '50%', border: 'none', cursor: 'pointer', fontSize: 8,
                    background: 'rgba(239,68,68,0.25)', color: '#ef4444',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 11,
                  }}>✕</button>
                </div>
              );
            })}

            {rooms.length === 0 && (
              <div style={{ position: 'absolute', left: '50%', top: '40%', transform: 'translate(-50%,-50%)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, pointerEvents: 'none' }}>
                <div style={{ fontSize: 44 }}>🏠</div>
                <div style={{ color: '#444', fontSize: 14 }}>{t('layoutEmptyHint')}</div>
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Sidebar */}
        <div style={{ width: 155, flexShrink: 0 }}>
          <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, letterSpacing: 1, marginBottom: 7 }}>
            {t('layoutUnplaced')} ({unplaced.length})
          </div>
          {unplaced.length === 0 ? (
            <div style={{ fontSize: 12, color: '#22c55e', marginBottom: 12 }}>{t('layoutAllPlaced')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
              {unplaced.map(c => {
                const col = pcCol(c);
                return (
                  <div key={c.id} onClick={() => placePC(c.id)} title={t('layoutClickTip')}
                    style={{ padding: '6px 10px', background: 'var(--bg2)', borderRadius: 7, cursor: 'pointer',
                      border: `1px solid ${col}33`, display: 'flex', alignItems: 'center', gap: 7, transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = col}
                    onMouseLeave={e => e.currentTarget.style.borderColor = col + '33'}
                  >
                    <span style={{ fontSize: 13, lineHeight: 1, flexShrink: 0 }}>{DEVICE_ICONS[c.type] || '🖥'}</span>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>#{c.number}</span>
                    {c.type !== 'pc' && <span style={{ fontSize: 9, color: DEVICE_COL[c.type] || 'var(--text3)', marginLeft: 'auto', fontWeight: 700 }}>{c.type === 'billiard' ? '🎱' : c.type === 'playstation' ? 'PS' : c.type}</span>}
                  </div>
                );
              })}
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2, lineHeight: 1.4 }}>
                {t('layoutClickTip')}
              </div>
            </div>
          )}

          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, letterSpacing: 1, marginBottom: 6 }}>{t('layoutStatusLabel')}</div>
            {STATUS_LEGEND.map(([col, tKey]) => (
              <div key={tKey} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: col, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: 'var(--text2)' }}>{t(tKey)}</span>
              </div>
            ))}
          </div>

          {rooms.length > 0 && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 10 }}>
              <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, letterSpacing: 1, marginBottom: 6 }}>{t('layoutRoomsLabel')}</div>
              {rooms.map((r, idx) => {
                const pal = PALETTE[r.color_index ?? idx % PALETTE.length];
                const cnt = Object.values(pcPos).filter(p => p.room_id === r.id).length;
                return (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: pal.border, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: 'var(--text2)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: pal.text }}>{cnt}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Tips */}
      <div style={{ marginTop: 10, fontSize: 10, color: '#444', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <span>{t('layoutTip1')}</span>
        <span>{t('layoutTip2')}</span>
        <span>{t('layoutTip3')}</span>
        <span>{t('layoutTip4')} Zoom ({Math.round(zoom * 100)}%)</span>
        <span>{t('layoutTip5')}</span>
      </div>
    </div>
  );
}
